/**
 * ToolLandingPage
 * ---------------------------------------------------------------------------
 * The shared public page behind every separately-marketable tool address
 * (/astrology, /career, /relationships, /enterprise, /liker). Renders one
 * registry entry from src/config/toolLandings.ts.
 *
 * Visitors can look, not start: the only action is sign-up, plus a quiet link
 * to the free personality test.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ArrowRight, Check, Clock, CreditCard, Lock, Quote, X } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { FloatingHeader } from '@/components/layout/FloatingHeader';
import { useAuth } from '@/hooks/useAuth';
import { useInPublicShell } from '@/components/layout/PublicPageShell';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { TOOL_SAMPLES, TOOL_SAMPLE_SET, TOOL_TRY_IT } from '@/components/tools/samples';
import { rememberToolIntent, toolSignupPath } from '@/lib/tools/toolIntent';
import { toolAccent, TOOL_GLYPHS, toolPattern } from '@/lib/tools/toolVisuals';
import { DISCORD_INVITE_URL } from '@/config/community';
import { TOOL_LANDING_LIST } from '@/config/toolLandings';
import type { ToolLanding } from '@/config/toolLandings';
import { cn } from '@/lib/utils';

interface ToolLandingPageProps {
  tool: ToolLanding;
}

/** Framed "peek into the product" panel around a static sample. */
const SampleFrame: React.FC<{
  accentClasses: ReturnType<typeof toolAccent>;
  children: React.ReactNode;
  className?: string;
}> = ({ accentClasses, children, className }) => (
  <div className={cn('relative', className)}>
    <div
      className={cn('pointer-events-none absolute -inset-6 rounded-[2rem] blur-3xl opacity-60', accentClasses.glow)}
      aria-hidden
    />
    <div
      className={cn(
        'relative rounded-2xl border bg-card/80 backdrop-blur-sm overflow-hidden shadow-2xl',
        accentClasses.ring,
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-2.5">
        <div className="flex items-center gap-1.5" aria-hidden>
          <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
          <span className="w-2 h-2 rounded-full bg-muted-foreground/25" />
          <span className="w-2 h-2 rounded-full bg-muted-foreground/25" />
        </div>
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Example output</span>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  </div>
);

export const ToolLandingPage: React.FC<ToolLandingPageProps> = ({ tool }) => {
  const { user } = useAuth();
  const inPublicShell = useInPublicShell();
  const ownHeader = !user && !inPublicShell;
  const Sample = TOOL_SAMPLES[tool.slug];
  const panels = TOOL_SAMPLE_SET[tool.slug] ?? [];
  const tryIt = TOOL_TRY_IT[tool.slug];
  const TryItComponent = tryIt?.Component;
  const Glyph = TOOL_GLYPHS[tool.slug];
  const a = toolAccent(tool.accent);
  const signupHref = toolSignupPath(tool.slug);
  const otherTools = TOOL_LANDING_LIST.filter((t) => t.slug !== tool.slug);

  // Track which example panel is centred so the phone strip can show feedback.
  const stripRef = useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = useState(0);
  const onStripScroll = () => {
    const el = stripRef.current;
    if (!el || el.children.length === 0) return;
    const first = el.children[0] as HTMLElement;
    const stride = first.offsetWidth + 16; // gap-4
    setActivePanel(Math.min(panels.length - 1, Math.round(el.scrollLeft / stride)));
  };

  // Remember the entry point so sign-up lands them back in this tool.
  useEffect(() => {
    rememberToolIntent(tool.slug);
  }, [tool.slug]);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: `Psyforge ${tool.label}`,
      description: tool.seo.description,
      url: `https://psyforge.com${tool.path}`,
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: tool.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-dvh bg-background">
      <SEO
        title={tool.seo.title}
        description={tool.seo.description}
        canonicalPath={tool.path}
        structuredData={structuredData}
      />
      {/* Signed-in members already have the app shell header — don't stack a second one. */}
      {ownHeader && <FloatingHeader />}

      {/* 1. Opening screen — promise beside the real thing */}
      <section className={cn('relative overflow-hidden pb-16 px-4', ownHeader ? 'pt-28' : 'pt-8')}>
        <div
          className={cn('pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-gradient-to-b', a.wash)}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={toolPattern(tool.slug)}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
          aria-hidden
        />

        <div className="relative max-w-6xl mx-auto grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 text-center lg:text-left"
          >
            <div className="flex items-center justify-center lg:justify-start gap-3">
              <span className={cn('w-11 h-11 rounded-xl flex items-center justify-center', a.tile)}>
                {Glyph && <Glyph className="w-6 h-6" />}
              </span>
              <Badge variant="outline" className={cn('uppercase tracking-wider', a.ring)}>
                {tool.label}
              </Badge>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-foreground leading-[1.05] tracking-tight">
              {tool.headlineAccent && tool.headline.includes(tool.headlineAccent) ? (
                <>
                  {tool.headline.slice(0, tool.headline.indexOf(tool.headlineAccent))}
                  <span className={a.text}>{tool.headlineAccent}</span>
                  {tool.headline.slice(tool.headline.indexOf(tool.headlineAccent) + tool.headlineAccent.length)}
                </>
              ) : (
                tool.headline
              )}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0">{tool.promise}</p>

            <div className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-3">
              <Button asChild size="lg" className="gap-2 w-full sm:w-auto">
                <Link to={signupHref}>
                  Create a free account
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 pt-1">
              {[
                { icon: Clock, label: tool.timeCost },
                { icon: CreditCard, label: 'No card needed' },
                { icon: Lock, label: 'Your results stay private' },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/50 px-3 py-1 text-xs text-muted-foreground"
                >
                  <Icon className={cn('w-3.5 h-3.5', a.text)} />
                  {label}
                </span>
              ))}
            </div>
          </motion.div>

          {/* The real thing, right away. */}
          <motion.div
            initial={{ opacity: 0, y: 26, rotate: -1.5 }}
            animate={{ opacity: 1, y: 0, rotate: -1.5 }}
            whileHover={{ rotate: 0, scale: 1.015 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="hidden lg:block"
          >
            <SampleFrame accentClasses={a}>{Sample && <Sample />}</SampleFrame>
          </motion.div>
        </div>
      </section>

      {/* 2. What you get */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto space-y-6">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <span className={cn('h-px w-8', a.bar)} aria-hidden />
              <h2 className="font-display text-2xl sm:text-3xl text-foreground">What you get</h2>
            </div>
          </ScrollReveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {tool.outputs.map((o, i) => (
              <ScrollReveal key={o.title} delay={i * 80}>
                <Card className="h-full p-5 bg-card/60 space-y-3 transition-all duration-300 hover:-translate-y-1 hover:border-border hover:shadow-xl">
                  <span className={cn('w-9 h-9 rounded-lg flex items-center justify-center', a.tile)}>
                    <Check className="w-4.5 h-4.5" strokeWidth={2.5} />
                  </span>
                  <div className="font-medium text-foreground">{o.title}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{o.body}</p>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. A closer look — a strip of labelled example panels.
          On desktop the first panel already sits in the hero, so it is hidden here. */}
      <section className={cn('px-4 pb-16', panels.length < 2 && 'lg:hidden')}>
        <div className="max-w-6xl mx-auto space-y-6">
          <ScrollReveal>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className={cn('h-px w-8', a.bar)} aria-hidden />
                <h2 className="font-display text-2xl sm:text-3xl text-foreground">A closer look</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Example output. Yours is generated from your own answers.
              </p>
            </div>
          </ScrollReveal>

          <div className="relative">
            <div
              ref={stripRef}
              onScroll={onStripScroll}
              className={cn(
                'flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4',
                'lg:mx-0 lg:px-0 lg:overflow-visible lg:grid',
                panels.length > 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-1',
              )}
            >
              {panels.map(({ label, Component }, i) => (
                <div
                  key={label}
                  className={cn(
                    'min-w-[85%] sm:min-w-[60%] lg:min-w-0 snap-center flex flex-col gap-2 transition-opacity duration-300',
                    i === 0 && panels.length > 1 && 'lg:hidden',
                    i !== activePanel && 'opacity-70 lg:opacity-100',
                  )}
                >
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>
                  <SampleFrame accentClasses={a} className="flex-1">
                    <Component />
                  </SampleFrame>
                </div>
              ))}
            </div>
            {/* Fade edge hinting there is more to swipe to (phones only). */}
            {panels.length > 1 && (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent lg:hidden"
                aria-hidden
              />
            )}
          </div>

          {/* Swipe hint + position dots (phones only). */}
          {panels.length > 1 && (
            <div className="flex items-center justify-center gap-3 lg:hidden">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Swipe</span>
              <div className="flex items-center gap-1.5">
                {panels.map(({ label }, i) => (
                  <span
                    key={label}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      i === activePanel ? cn('w-4', a.bar) : 'w-1.5 bg-muted-foreground/30',
                    )}
                    aria-hidden
                  />
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* 3a. Try it yourself — only where a genuine guest surface exists */}
      {tryIt && (
        <section className="px-4 pb-16">
          <div className="max-w-4xl mx-auto space-y-6">
            <ScrollReveal>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className={cn('h-px w-8', a.bar)} aria-hidden />
                  <h2 className="font-display text-2xl sm:text-3xl text-foreground">{tryIt.title}</h2>
                </div>
                <p className="text-sm text-muted-foreground">{tryIt.blurb}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <Card className={cn('p-4 sm:p-6 bg-card/60', a.ring)}>
                {TryItComponent && <TryItComponent />}
              </Card>
            </ScrollReveal>
          </div>
        </section>
      )}



      {/* 3b. Who it's for */}
      {tool.audienceFit && (
        <section className="px-4 pb-16">
          <div className="max-w-5xl mx-auto space-y-6">
            <ScrollReveal>
              <div className="flex items-center gap-3">
                <span className={cn('h-px w-8', a.bar)} aria-hidden />
                <h2 className="font-display text-2xl sm:text-3xl text-foreground">Who this is for</h2>
              </div>
            </ScrollReveal>
            <div className="grid gap-4 md:grid-cols-2">
              <ScrollReveal>
                <Card className={cn('h-full p-5 bg-card/60 space-y-3', a.ring)}>
                  <div className="flex items-center gap-2">
                    <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center', a.tile)}>
                      <Check className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <span className="font-medium text-foreground">A good fit if</span>
                  </div>
                  <ul className="space-y-2.5">
                    {tool.audienceFit.for.map((line) => (
                      <li key={line} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                        <span className={cn('mt-2 h-1.5 w-1.5 rounded-full shrink-0', a.bar)} aria-hidden />
                        {line}
                      </li>
                    ))}
                  </ul>
                </Card>
              </ScrollReveal>
              <ScrollReveal delay={80}>
                <Card className="h-full p-5 bg-card/40 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-muted/50 text-muted-foreground flex items-center justify-center">
                      <X className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <span className="font-medium text-foreground">Probably not if</span>
                  </div>
                  <ul className="space-y-2.5">
                    {tool.audienceFit.notFor.map((line) => (
                      <li key={line} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" aria-hidden />
                        {line}
                      </li>
                    ))}
                  </ul>
                </Card>
              </ScrollReveal>
            </div>
          </div>
        </section>
      )}


      {/* 4. How it works */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto space-y-6">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <span className={cn('h-px w-8', a.bar)} aria-hidden />
              <h2 className="font-display text-2xl sm:text-3xl text-foreground">How it works</h2>
            </div>
          </ScrollReveal>
          <div className="relative">
            {/* Connecting line on desktop */}
            <div
              className="hidden sm:block absolute left-[12%] right-[12%] top-9 h-px bg-gradient-to-r from-transparent via-border to-transparent"
              aria-hidden
            />
            <div className="relative grid gap-4 sm:grid-cols-3">
              {tool.steps.map((s, i) => (
                <ScrollReveal key={s.title} delay={i * 90}>
                  <Card className="h-full p-5 bg-card/70 backdrop-blur-sm space-y-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'w-9 h-9 rounded-full text-sm font-semibold flex items-center justify-center',
                          a.tile,
                        )}
                      >
                        {i + 1}
                      </span>
                      {s.time && (
                        <span className="rounded-full border border-border/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                          {s.time}
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-foreground">{s.title}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4b. What it's built on */}
      {tool.builtOn && (
        <section className="px-4 pb-16">
          <ScrollReveal>
            <div
              className={cn(
                'relative max-w-5xl mx-auto overflow-hidden rounded-2xl border bg-card/40 px-6 py-8 text-center space-y-4',
                a.ring,
              )}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={toolPattern(tool.slug)}
                aria-hidden
              />
              <div className="relative space-y-4">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">What it's built on</p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {tool.builtOn.items.map((item) => (
                    <span
                      key={item}
                      className={cn(
                        'rounded-full border bg-background/40 px-3 py-1 text-xs text-foreground',
                        a.ring,
                      )}
                    >
                      {item}
                    </span>
                  ))}
                </div>
                {tool.builtOn.note && (
                  <p className="text-sm text-muted-foreground max-w-xl mx-auto">{tool.builtOn.note}</p>
                )}
                <Link to="/science" className={cn('inline-flex items-center gap-1 text-sm underline', a.text)}>
                  How the methodology works
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* 4c. The kind of thing it tells you */}
      {tool.voices && tool.voices.length > 0 && (
        <section className="px-4 pb-16">
          <div className="max-w-5xl mx-auto space-y-6">
            <ScrollReveal>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className={cn('h-px w-8', a.bar)} aria-hidden />
                  <h2 className="font-display text-2xl sm:text-3xl text-foreground">
                    The kind of thing it tells you
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Illustrative lines, not customer reviews — written the way the tool actually writes.
                </p>
              </div>
            </ScrollReveal>
            <div className="grid gap-4 md:grid-cols-3">
              {tool.voices.map((v, i) => (
                <ScrollReveal key={v.quote} delay={i * 80}>
                  <Card className="relative h-full overflow-hidden p-5 pl-6 bg-card/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    <span
                      className={cn('absolute inset-y-0 left-0 w-[3px]', a.bar)}
                      aria-hidden
                    />
                    <div className="space-y-3">
                      <Quote className={cn('w-7 h-7 opacity-80', a.text)} strokeWidth={1.5} aria-hidden />
                      <p className="text-sm text-foreground leading-relaxed">{v.quote}</p>
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{v.source}</p>
                    </div>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* 5. Questions this audience actually asks */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto space-y-5">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <span className={cn('h-px w-8', a.bar)} aria-hidden />
              <h2 className="font-display text-2xl sm:text-3xl text-foreground">Questions</h2>
            </div>
          </ScrollReveal>
          <Accordion type="single" collapsible className="w-full">
            {tool.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-border/60">
                <AccordionTrigger className="text-left text-base py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 6. One call to action */}
      <section className="px-4 pb-16">
        <div
          className={cn(
            'relative max-w-5xl mx-auto overflow-hidden rounded-3xl border bg-card/50 px-6 py-14 text-center',
            a.ring,
          )}
        >
          <div
            className={cn('pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t', a.wash)}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={toolPattern(tool.slug)}
            aria-hidden
          />
          <div className="relative space-y-5">
            <span className={cn('mx-auto w-12 h-12 rounded-xl flex items-center justify-center', a.tile)}>
              {Glyph && <Glyph className="w-6 h-6" />}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Ready to start?</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Create a free account and you'll go straight into {tool.label.toLowerCase()}.
            </p>
            <span className="group relative inline-block">
              <span
                className={cn(
                  'pointer-events-none absolute -inset-3 rounded-full blur-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100',
                  a.glow,
                )}
                aria-hidden
              />
              <Button asChild size="lg" className="relative gap-2">
                <Link to={signupHref}>
                  Create a free account
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </span>
            <p className="text-xs text-muted-foreground">
              Just browsing?{' '}
              <Link to="/" className="underline hover:text-foreground">
                Start with the free personality test
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* 7. Somewhere to go next */}
      <footer className="border-t border-border/60 px-4 py-12">
        <div className="max-w-5xl mx-auto grid gap-8 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Other tools</p>
            <ul className="space-y-2">
              {otherTools.map((t) => {
                const OtherGlyph = TOOL_GLYPHS[t.slug];
                return (
                  <li key={t.slug}>
                    <Link
                      to={t.path}
                      className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {OtherGlyph && <OtherGlyph className="w-4 h-4" />}
                      {t.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Start here</p>
            <ul className="space-y-2">
              <li>
                <Link to="/tps-onboarding" className="text-muted-foreground hover:text-foreground transition-colors">
                  Free personality test
                </Link>
              </li>
              <li>
                <Link to="/quizzes" className="text-muted-foreground hover:text-foreground transition-colors">
                  Free quizzes
                </Link>
              </li>
              <li>
                <Link to="/demo" className="text-muted-foreground hover:text-foreground transition-colors">
                  Guided tour
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Learn</p>
            <ul className="space-y-2">
              <li>
                <Link to="/science" className="text-muted-foreground hover:text-foreground transition-colors">
                  Methodology
                </Link>
              </li>
              <li>
                <a
                  href={DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Community on Discord
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Account</p>
            <ul className="space-y-2">
              <li>
                <Link to={signupHref} className="text-muted-foreground hover:text-foreground transition-colors">
                  Create an account
                </Link>
              </li>
              <li>
                <Link to="/auth" className="text-muted-foreground hover:text-foreground transition-colors">
                  Sign in
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <p className="max-w-5xl mx-auto pt-8 text-xs text-muted-foreground">
          Psyforge — measured personality, put to work. Results are for self-understanding, not clinical or hiring
          decisions.
        </p>
      </footer>
    </div>

  );
};

export default ToolLandingPage;
